import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useApi } from '../apiHelpers';
import { DataTable } from '../components/table/DataTable';
import PageLayout from '../components/ui/PageLayout';
import { Chip, ChipGroup } from '../components/ui/Chip';
import { SegTrack } from '../components/ui/SegTrack';
import { Pager } from '../components/ui/Pager';
import LogoSpinner from '../components/ui/LogoSpinner';
import { EmptyState } from '../components/ui/EmptyState';

// This page PAGINATES, so the sort has to be the page's own. DataTable's
// `sortable` orders only the rows it is handed, and this table is handed one
// page of 25 out of ~16k — a header click would reorder those 25 and read as
// if it had sorted the lot. So the order lives here: sort the whole merged
// array, THEN slice. Same reason FirmwareLabels keeps its buttons page-owned,
// and the same shape: the strip carries them, no `sortable` goes to the table.
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'drone', label: 'By drone' },
];

const PAGE_SIZE = 25;

// sortDate is an 18-digit STRING: YYMMDDHHmmssSSS plus a 3-digit tie-break the
// arrange functions add so two entries in the same millisecond still have an
// order. 18 digits is past Number.MAX_SAFE_INTEGER (doubles are spaced 16 apart
// at that magnitude), so Number() rounds away exactly the low digits the
// tie-break lives in: two rows one millisecond apart differ by 1000 and compare
// fine, two inside the same millisecond compare equal by accident. Same-length
// all-digit strings compare digit by digit, so `<` on the strings IS the
// numeric comparison, to the last digit and with no rounding.
const olderFirst = (a, b) => (a.sortDate < b.sortDate ? -1 : a.sortDate > b.sortDate ? 1 : 0);
const newerFirst = (a, b) => olderFirst(b, a);

// Per-row status tint, so it rides `rowProps` rather than a column class (a
// column class would tint every row). Rest tone only: the vessel owns hover,
// and restating a hover here would be two `hover:bg-*` utilities on one
// element resolving by stylesheet order. The text tone stays, so a flag or a
// report is still legible as one under the pointer.
const ROW_TINT = {
  flag: 'bg-yellow-900/15 text-yellow-200',
  report: 'bg-purple-900/15 text-purple-200',
};

// ---------------------------------------------------------------------------
// Columns for the shared DataTable.
//
// Module scope per the DataTable contract: the array never changes identity,
// so no cell subtree is remounted on a poll. It also fixes the old memo's dead
// `[drones]` dep by construction — there are no deps to get wrong. Nothing
// here is volatile, so the table needs no `meta`.
//
// Display-only columns (`{id, cell}`), deliberately: no accessorFn, because
// nothing on this table sorts. The order is the page's, above.
//
// Plain JavaScript file: object literals, and <DataTable> with no type
// argument. The generic exists for the .tsx consumers and costs this file
// nothing.
// ---------------------------------------------------------------------------

const LOG_COLUMNS = [
  {
    id: 'date',
    header: 'Date',
    meta: { headerClass: 'w-[150px]' },
    cell: ({ row }) => row.original.date,
  },
  {
    id: 'drone_serial',
    header: 'Drone ID',
    meta: { headerClass: 'w-[120px]' },
    cell: ({ row }) => {
      const serial = row.original.drone_serial.slice(-7);
      return (
        <Link
          to={`/drone/${row.original.drone_serial}`}
          className="inline-flex items-center gap-1.5 font-bold text-primary transition-all duration-200
                hover:drop-shadow-[0_0_8px_rgba(255,204,51,0.6)]"
        >
          {serial}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            fill="currentColor"
            viewBox="0 0 16 16"
            className="opacity-50"
          >
            <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"/>
          </svg>
        </Link>
      );
    },
  },
  {
    id: 'user',
    header: 'User',
    meta: { headerClass: 'w-[120px]' },
    cell: ({ row }) => row.original.user,
  },
  {
    id: 'desc',
    header: 'Description',
    cell: ({ row }) => {
      const value = row.original.desc;
      if (typeof value === 'string' && value.startsWith('http')) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="text-blue-400 underline"
          >
            LOG PULLED
          </a>
        );
      }
      return value;
    },
  },
];

const MaintLogPage = () => {
  const api = useApi();
  const [maintLogs, setMaintLogs] = useState([]);
  const [flags, setFlags] = useState([]);
  const [reports, setReports] = useState([]);
  const [partsReady, setPartsReady] = useState(false);
  const [logTypeSelection, setLogTypeSelection] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [pageIndexState, setPageIndexState] = useState(0);

  // Format maintenance logs.
  const arrangeMaint = (logs) =>
    logs.map((log) => {
      const logDate = dayjs(log.dateofservice).format('MMM-DD YY HH:mm');
      // Create a sortable numeric string.
      const sortDate = `${dayjs(log.dateofservice).format('YYMMDDHHmmssSSS')}${Math.floor(
        100 + Math.random() * 900
      )}`;
      return {
        date: logDate,
        sortDate,
        desc: log.descript,
        drone_serial: log.drone_serial,
        user: log.firstname || 'Unknown',
        status: 'maint',
      };
    });

  // Format flag logs.
  const arrangeFlags = (flags) => {
    const arranged = flags.map((log) => {
      const logDate = dayjs(log.dateflagged).format('MMM-DD YY HH:mm');
      const sortDate = `${dayjs(log.dateflagged).format('YYMMDDHHmmssSSS')}${Math.floor(
        100 + Math.random() * 900
      )}`;
      return {
        date: logDate,
        sortDate,
        desc: log.descript,
        drone_serial: log.drone_serial,
        user: log.firstname || 'Unknown',
        status: 'flag',
      };
    });
    return arranged;
  };

  // Format report logs.
  const arrangeReports = (reports) => {
    return reports.map((report) => {
      const logDate = dayjs(report.date_reported).format('MMM-DD YY HH:mm');
      const sortDate = `${dayjs(report.date_reported).format('YYMMDDHHmmssSSS')}${Math.floor(
        100 + Math.random() * 900
      )}`;
      return {
        date: logDate,
        sortDate,
        desc: report.issue_message,
        drone_serial: report.drone_serial,
        user: report.server_id || 'Unknown',
        status: 'report',
      };
    });
  };

  // Fetch maintenance, flag, and report logs.
  const getLogs = async () => {
    try {
      const maintData = await api.get(`/maintenance`, {
        errorAlert: 'Logs not found, please try again'
      });
      setMaintLogs(arrangeMaint(maintData));
    } catch (error) {
      // Error alert already handled by api.get
    }

    try {
      const flagsData = await api.get(`/flags`, {
        silent: true
      });
      setFlags(arrangeFlags(flagsData));
    } catch (error) {
      console.log('Flags not found, please try again');
    }

    try {
      const reportsData = await api.get(`/reports?limit=500`, {
        silent: true
      });
      setReports(arrangeReports(reportsData.reports || []));
    } catch (error) {
      console.log('Reports not found');
    }

    setPartsReady(true);
  };

  useEffect(() => {
    getLogs();
    const interval = setInterval(getLogs, 5000);
    return () => clearInterval(interval);
  }, []);

  // Combine logs based on selection.
  const listItems = useMemo(() => {
    if (logTypeSelection === 'all') return [...maintLogs, ...flags, ...reports];
    if (logTypeSelection === 'maint') return maintLogs;
    if (logTypeSelection === 'flags') return flags;
    if (logTypeSelection === 'reports') return reports;
    return [];
  }, [maintLogs, flags, reports, logTypeSelection]);

  // The WHOLE pile, ordered — before any slicing. Newest is the default and
  // reproduces the old table's `[{ id: 'date', desc: true }]`: sortDate is the
  // YYMMDDHHmmssSSS key the arrange functions build, so descending on it is
  // date-descending. Copied first, because 'maint' / 'flags' / 'reports' hand
  // back the state array itself.
  const sortedItems = useMemo(() => {
    const items = [...listItems];
    if (sortBy === 'oldest') {
      return items.sort(olderFirst);
    }
    if (sortBy === 'drone') {
      return items.sort(
        (a, b) => a.drone_serial.localeCompare(b.drone_serial) || newerFirst(a, b)
      );
    }
    return items.sort(newerFirst);
  }, [listItems, sortBy]);

  // Page count the way tanstack's getPageCount reported it, zero included, so
  // the controls below read exactly as they did.
  const pageCount = Math.ceil(sortedItems.length / PAGE_SIZE);
  // Clamped on READ, never written back. The 5s poll must not move the reader
  // (that is what the old table's autoResetPageIndex: false bought), and an
  // effect that clamped into state would do exactly that on any batch that
  // came back shorter. Logs only ever grow, so this normally never bites.
  const pageIndex = Math.min(pageIndexState, Math.max(0, pageCount - 1));

  // Sort whole, THEN slice. The other order would page through a stale
  // ordering and show the wrong 25 rows.
  const pageRows = useMemo(
    () => sortedItems.slice(pageIndex * PAGE_SIZE, pageIndex * PAGE_SIZE + PAGE_SIZE),
    [sortedItems, pageIndex]
  );

  // Both of these reorder or replace the whole pile, so page 7 of the old pile
  // means nothing. Set at the click rather than in an effect: an effect on
  // `listItems` would also fire on every poll and undo the paragraph above.
  const selectLogType = (value) => {
    setLogTypeSelection(value);
    setPageIndexState(0);
  };
  const selectSort = (value) => {
    setSortBy(value);
    setPageIndexState(0);
  };

  if (!partsReady) return <LogoSpinner />;

  return (
    <PageLayout kind="workspace" className="pt-2 md:pt-4">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-1">Maintenance Logs</h1>
        <p className="text-on-surface-variant text-sm">
          {listItems.length} total entries
        </p>
      </div>

      {/* Filter Buttons */}
      <ChipGroup label="Log type" className="mb-4">
        {[
          { value: 'all', label: 'All Logs', count: maintLogs.length + flags.length + reports.length },
          { value: 'maint', label: 'Maintenance', count: maintLogs.length },
          { value: 'flags', label: 'Flags', count: flags.length },
          { value: 'reports', label: 'Reports', count: reports.length },
        ].map((f) => (
          <Chip
            key={f.value}
            pressed={logTypeSelection === f.value}
            onClick={() => selectLogType(f.value)}
            count={f.count}
          >
            {f.label}
          </Chip>
        ))}
      </ChipGroup>

      {/* The table. No box around it: the shared table renders its own vessel,
          so one here would draw a second one around the real one. The sort
          buttons ride the working strip, sitting on the pile they reorder. */}
      <DataTable
        data={pageRows}
        columns={LOG_COLUMNS}
        strip={
          <>
            <span className="text-xs text-on-surface-variant">Sort by:</span>
            <SegTrack
              label="Sort by"
              size="sm"
              options={SORT_OPTIONS.map((o) => ({ key: o.value, label: o.label }))}
              value={sortBy}
              onChange={selectSort}
            />
          </>
        }
        rowProps={(item) => ({ className: ROW_TINT[item.status] ?? '' })}
        empty={<EmptyState variant="area">No logs found</EmptyState>}
      />

      {/* Pagination Controls. Page-owned and page-local: the component has no
          pagination, so the state, the slice above and these controls are all
          this page's. */}
      <div className="mt-4 flex flex-wrap justify-between items-center gap-4 text-white">
        <Pager
          page={pageIndex}
          pageCount={pageCount}
          onPage={setPageIndexState}
          total={sortedItems.length}
          pageSize={PAGE_SIZE}
          label="Pages of logs"
          align="start"
        />
        {/* Still off, as it has been. Kept verbatim, so note that its two
            handlers predate the page-owned pagination above: turning it back on
            means a pageSize state where PAGE_SIZE is now a constant, not
            uncommenting these lines.
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="bg-surface-container-high shadow-recess text-on-surface text-sm px-3 py-2 rounded
            focus:outline-none focus:ring-2 focus:ring-primary/70
            transition-all duration-200"
        >
          {[10, 25, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select> */}
      </div>
    </PageLayout>
  );
};

export default MaintLogPage;
